/**
 * Data-access layer — the seam between the frontend and the backend API.
 *
 * All screens and components read data through hooks.ts — nothing here is
 * imported directly by screens. This file is the only place that knows the
 * backend URL structure.
 */

import axios from "axios";
import { useAuthStore } from "./authStore";
import type {
  Programme,
  Project,
  SpendRequest,
  SpendItem,
  EvidenceItem,
  Organisation,
  Person,
  AuditEvent,
  Report,
  SiteVisit,
  SiteVisitQuestion,
  AuthorityGrant,
  WorkspaceSeed,
} from "./types";
import type { ActionItem } from "./actionPanel";

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "/api",
  headers: { "Content-Type": "application/json" },
  // Send the httpOnly session cookie set at login; the server's CORS config
  // allows credentials from the configured origin. Without this, /auth/me has
  // no cookie after a page refresh and the session is lost.
  withCredentials: true,
});

// Attach the JWT as a Bearer header when this tab still has one in memory
// (set on login/`/auth/me`, never persisted to localStorage - see
// authStore.ts). This is genuinely optional: the server's jwt_authorisation
// cookie is httpOnly (server/routers/authentication.js), so it's invisible
// to this JS and can't be read here to attach explicitly - but it's sent
// automatically by the browser via withCredentials above, and the server's
// getTokenFromRequest falls back to it whenever no Authorization header is
// present. So a request with no in-memory token yet (e.g. right after a hard
// refresh, before /auth/me resolves) still authenticates correctly via the
// cookie alone.
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Expose 401s so auth layer can redirect to login
apiClient.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      window.dispatchEvent(new Event("il:unauthorized"));
    }
    return Promise.reject(err);
  },
);

// ---------------------------------------------------------------------------
// Runtime demo escape hatch
// ---------------------------------------------------------------------------

/**
 * True while the runtime "Developer mode" toggle is on (persisted by the store
 * under localStorage key "il.devMode"). When true, mutations run optimistically
 * against the local seed store instead of the backend — so the profile switcher
 * can drive the whole loop without real per-persona logins or backend id
 * coupling. Default off: normal operation is API-first. (The old build-time
 * VITE_DEMO_MODE flag was removed.)
 */
export function demoModeActive(): boolean {
  try {
    return localStorage.getItem("il.devMode") === "true";
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Input types for mutations
// ---------------------------------------------------------------------------

export interface RaiseSpendInput {
  /** Numeric DB id or seed slug — the backend resolves either. */
  projectId: string;
  subProjectId?: string;
  milestoneId?: string;
  title: string;
  /** The narrative for the ask. `description` is the legacy alias. */
  purpose?: string;
  description?: string;
  amount: number;
  category?: string;
  /** Defaults to the project's currency server-side. */
  currency?: string;
  supplier?: string;
  targetSupplier?: string;
  lineItems?: { description: string; amount: number; category: string }[];
}

export interface ApprovalDecision {
  action: "approve" | "reject" | "query";
  note?: string;
}

export interface EvidenceInput {
  askId: string;
  type: "photo" | "receipt" | "document";
  /** The real picked file, uploaded to Cloudinary server-side (see
   * evidenceFileStore.js). Optional - evidence with just a note/geo and no
   * file is still valid. */
  file?: File;
  note?: string;
  lat?: number;
  lng?: number;
}

/** A project (or sub-project) the current user may invite people into, with the
 * cascade roles they are allowed to appoint there (§9.5 invite scope). */
export interface InvitableSubProject {
  /** DB id (production) or seed slug (demo) — identifies the node for display
   * and selection. */
  projectId: number | string;
  name: string;
  slug: string;
  myRole: string;
  /** Cascade role keys, e.g. ["prime_member", "sub_lead"]. */
  appointableRoles: string[];
  /** The id actually sent to createInvite when this node is chosen. Defaults to
   * `projectId`. Set separately only in demo mode for sub-projects, whose seed
   * ids have no backend counterpart — there it points at the parent project's
   * slug so the generated invite is real and openable (scoped to the parent). */
  inviteTargetId?: number | string;
}
export interface InvitableProject extends InvitableSubProject {
  subProjects: InvitableSubProject[];
}

// ---------------------------------------------------------------------------
// Query functions (used by TanStack Query in production mode)
// ---------------------------------------------------------------------------

export const api = {
  // ── Workspace seeds (boot hydration) ─────────────────────────────────────
  getWorkspaceSeeds: (): Promise<Record<string, WorkspaceSeed>> =>
    apiClient
      .get<{ seeds: Record<string, WorkspaceSeed> }>("/workspace-seed")
      .then((r) => r.data.seeds),

  // The dynamic "actions needed" rail (server/helpers/actionPanel.js) -
  // always means "my own items"; persona is resolved server-side from the
  // authenticated user, not passed as a param.
  getActionPanelItems: (): Promise<ActionItem[]> =>
    apiClient.get<{ items: ActionItem[] }>("/action-panel").then((r) => r.data.items),

  // ── Programmes ──────────────────────────────────────────────────────────
  getProgrammes: (): Promise<Programme[]> =>
    apiClient.get<Programme[]>("/programme").then((r) => r.data),

  getProgramme: (id: string): Promise<Programme> =>
    apiClient.get<Programme>(`/programme/${id}`).then((r) => r.data),

  // ── Projects ────────────────────────────────────────────────────────────
  getProjects: (programmeId: string): Promise<Project[]> =>
    apiClient
      .get<Project[]>(`/project`, { params: { programmeId } })
      .then((r) => r.data),

  getProject: (projectId: string): Promise<Project> =>
    apiClient
      .get<Project>(`/project-management/project/${projectId}`)
      .then((r) => r.data),

  getSubProjects: (projectId: string): Promise<unknown[]> =>
    apiClient
      .get<{ message: string; data: unknown[] }>(`/project-management/project/${projectId}/sub-projects`)
      .then((r) => r.data.data ?? []),

  // ── Project creation & requests (ZEUS-125/127/129) ─────────────────────
  // stratus_admin only — server enforces, this is a raw create call, not a
  // typed Project (the ZEUS status model is disjoint from the demo Project
  // type's status union, see ZEUS-132/133's implementation notes).
  createZeusProject: (input: CreateZeusProjectInput): Promise<CreateZeusProjectResult> =>
    apiClient.post<CreateZeusProjectResult>("/zeus-project", input).then((r) => r.data),

  // Follow-up metadata (region/district/budgeted) — chained after create when
  // the modal collected any of them, since the create endpoint doesn't accept
  // them directly.
  editZeusProject: (
    projectId: number | string,
    input: EditZeusProjectInput,
  ): Promise<CreateZeusProjectResult> =>
    apiClient.patch<CreateZeusProjectResult>(`/zeus-project/${projectId}`, input).then((r) => r.data),

  // ZEUS-142 — direct status toggle among draft/review/published, no
  // adjacency restriction (matches the backend, which permits any-to-any).
  changeZeusProjectStatus: (
    projectId: number | string,
    input: ChangeZeusProjectStatusInput,
  ): Promise<CreateZeusProjectResult> =>
    apiClient
      .patch<CreateZeusProjectResult>(`/zeus-project/${projectId}/status`, input)
      .then((r) => r.data),

  // "Assign lead contractor" — a direct stratus_admin action for an
  // organisation that's already active on the platform, distinct from
  // inviting a brand-new one through the full cascade/onboarding flow.
  getLeadContractorCandidates: (
    projectId: number | string,
    q: string,
  ): Promise<LeadContractorCandidate[]> =>
    apiClient
      .get<{ candidates: LeadContractorCandidate[] }>(`/zeus-project/${projectId}/lead-contractor-candidates`, { params: { q } })
      .then((r) => r.data.candidates),

  assignLeadContractor: (
    projectId: number | string,
    input: AssignLeadContractorInput,
  ): Promise<CreateZeusProjectResult> =>
    apiClient
      .patch<CreateZeusProjectResult>(`/zeus-project/${projectId}/lead-contractor`, input)
      .then((r) => r.data),

  // Real org roster for the Team tab: this project's own lead contractor
  // plus each of its direct sub-projects' — org<->project is one-per-row via
  // leadcontractorid, there's no many-to-many link, so this is the whole set.
  getProjectOrganisations: (projectId: number | string): Promise<ProjectOrganisationsResult> =>
    apiClient
      .get<ProjectOrganisationsResult>(`/zeus-project/${projectId}/organisations`)
      .then((r) => r.data),

  // Contractor/subcontractor only — server enforces.
  submitProjectCreationRequest: (
    input: SubmitProjectCreationRequestInput,
  ): Promise<SubmitProjectCreationRequestResult> =>
    apiClient
      .post<SubmitProjectCreationRequestResult>("/project-creation-request", input)
      .then((r) => r.data),

  // ZEUS-138 — the stratus_admin review queue. Server-enforced; no join to a
  // requester display name (see ProjectCreationRequest — this app never
  // resolves that for real ZEUS users, only actorrole).
  getProjectCreationRequests: (status = "pending"): Promise<ProjectCreationRequest[]> =>
    apiClient
      .get<{ requests: ProjectCreationRequest[] }>("/project-creation-request", { params: { status } })
      .then((r) => r.data.requests),

  getProjectCreationRequest: (id: number | string): Promise<ProjectCreationRequest> =>
    apiClient
      .get<{ request: ProjectCreationRequest }>(`/project-creation-request/${id}`)
      .then((r) => r.data.request),

  rejectProjectCreationRequest: (
    id: number | string,
    reason: string,
  ): Promise<ProjectCreationRequest> =>
    apiClient
      .post<{ request: ProjectCreationRequest }>(`/project-creation-request/${id}/reject`, { reason })
      .then((r) => r.data.request),

  // ZEUS-144 — contractor edit-review cycle for PUBLISHED projects.
  // Contractor/subcontractor member of the project only — server enforces.
  submitProjectEditRequest: (
    input: SubmitProjectEditRequestInput,
  ): Promise<ProjectEditRequest> =>
    apiClient
      .post<{ request: ProjectEditRequest }>("/project-edit-request", input)
      .then((r) => r.data.request),

  // The stratus_admin review queue, scoped to one project (unlike
  // project-creation-request's platform-wide queue).
  getProjectEditRequests: (
    projectId: number | string,
    status = "pending",
  ): Promise<ProjectEditRequest[]> =>
    apiClient
      .get<{ requests: ProjectEditRequest[] }>("/project-edit-request", { params: { projectId, status } })
      .then((r) => r.data.requests),

  approveProjectEditRequest: (id: number | string): Promise<ProjectEditRequest> =>
    apiClient
      .post<{ request: ProjectEditRequest }>(`/project-edit-request/${id}/approve`)
      .then((r) => r.data.request),

  rejectProjectEditRequest: (id: number | string, reason: string): Promise<ProjectEditRequest> =>
    apiClient
      .post<{ request: ProjectEditRequest }>(`/project-edit-request/${id}/reject`, { reason })
      .then((r) => r.data.request),

  // ZEUS-135 — a project's governed-action audit entries (creation, direct
  // edits, status changes, edit-review cycle), reads ledger_event directly.
  getProjectGovernanceHistory: (projectId: number | string): Promise<GovernanceHistoryEvent[]> =>
    apiClient
      .get<{ events: GovernanceHistoryEvent[] }>(`/zeus-project/${projectId}/governance-history`)
      .then((r) => r.data.events),

  // ── Memberships ─────────────────────────────────────────────────────────
  // Projects (+ sub-projects) the authed user may invite people into (§9.5).
  getInvitableProjects: (): Promise<InvitableProject[]> =>
    apiClient
      .get<InvitableProject[]>("/project-membership/invitable-projects")
      .then((r) => r.data),

  // "Add members" — search platform users by name/email to add one directly
  // to a project, distinct from Invite's email-an-outsider flow. Excludes
  // users already on the project.
  searchProjectUsers: (projectId: number | string, q: string): Promise<SearchableUser[]> =>
    apiClient
      .get<SearchableUser[]>(`/project-membership/${projectId}/searchable-users`, { params: { q } })
      .then((r) => r.data),

  addProjectMember: (
    projectId: number | string,
    input: AddMemberInput,
  ): Promise<AddMemberResult> =>
    apiClient
      .post<AddMemberResult>(`/project-membership/${projectId}/members`, input)
      .then((r) => r.data),

  // Real project-membership rows (not the demo seed) — who's actually on this
  // project and whether they've accepted yet.
  getProjectMembers: (projectId: number | string): Promise<ProjectMemberRow[]> =>
    apiClient
      .get<{ members: ProjectMemberRow[] }>(`/project-membership/project/${projectId}`)
      .then((r) => r.data.members),

  // The signed-in user's own project ids, project-membership-scoped (no
  // :projectId query param hits GET /project-membership/'s "my projects"
  // branch). Used at login to resolve which workspace a contractor/reporter
  // actually belongs to (see resolveHomeWorkspaceId in hooks.ts) — the
  // active workspace is otherwise just whatever was last browsed by any
  // persona on this device, real or demo, with nothing pinning it to the
  // real signed-in user's own project(s). A 404 means "no memberships",
  // not an error — the route itself 404s on an empty result.
  getMyProjectIds: (): Promise<string[]> =>
    apiClient
      .get<{ projectid: number }[]>("/project-membership")
      .then((r) => r.data.map((row) => String(row.projectid)))
      .catch((e) => {
        if (e?.response?.status === 404) return [];
        throw e;
      }),

  // Soft-revoke (status='revoked') via the existing cascade.removeMember
  // helper - "you can only remove who you could have appointed," so this
  // naturally covers both oversight removing anyone and a prime_lead/sub_lead
  // removing their own appointees.
  removeProjectMember: (membershipId: number, justification: string): Promise<{ membership: unknown }> =>
    apiClient
      .post<{ membership: unknown }>(`/project-membership/${membershipId}/remove`, { justification })
      .then((r) => r.data),

  // ── Milestones ──────────────────────────────────────────────────────────
  getMilestones: (projectId: string): Promise<unknown[]> =>
    apiClient
      .get<{ message: string; data: unknown[] }>(`/project-management/project/${projectId}/milestones`)
      .then((r) => r.data.data ?? []),

  createMilestone: (
    projectId: string,
    input: { milestonenumber: string; description: string; targetDate?: string; plannedBudget?: number },
  ): Promise<unknown> =>
    apiClient
      .post<{ message: string; data: unknown }>(`/project-management/project/${projectId}/milestones`, {
        milestonenumber: input.milestonenumber,
        description: input.description,
        ...(input.targetDate && { enddate: input.targetDate }),
        ...(input.plannedBudget != null && { budget: input.plannedBudget }),
      })
      .then((r) => r.data.data),

  updateMilestone: (
    milestoneId: string,
    patch: { targetDate?: string; plannedBudget?: number; evidenceRequirements?: string[] },
  ): Promise<void> =>
    apiClient
      .patch(`/project-management/milestones/${milestoneId}`, {
        ...(patch.targetDate !== undefined && { enddate: patch.targetDate }),
        ...(patch.plannedBudget !== undefined && { budget: patch.plannedBudget }),
        ...(patch.evidenceRequirements !== undefined && { evidencerequirements: patch.evidenceRequirements }),
      })
      .then(() => undefined),

  // ── Notifications ────────────────────────────────────────────────────────
  // NotificationsBell was seed-only until now (see useNotifications.ts) —
  // these are thin wrappers over server/routers/notification.js, already
  // fully working, just never called from the client before.
  getNotifications: (
    { limit = 20 }: { limit?: number } = {},
  ): Promise<unknown[]> =>
    apiClient
      .get<{ notifications: unknown[] }>("/notification", { params: { limit } })
      .then((r) => r.data.notifications ?? []),

  markNotificationRead: (id: string): Promise<void> =>
    apiClient.patch(`/notification/${id}/read`).then(() => undefined),

  markAllNotificationsRead: (): Promise<void> =>
    apiClient.patch("/notification/read-all").then(() => undefined),

  // ── Spend requests (ZEUS spend flow — /api/approval) ────────────────────
  // Raw DB rows (select("*") server-side, same shape as getApproverQueue/
  // getMySpendRequests below) - not yet mapped to the camelCase SpendRequest
  // shape, so callers normalize (see ApprovalQueueTab.tsx's
  // normalizeSpendRequestRow) the same way those two already require.
  getSpendRequests: (projectId: string): Promise<Record<string, unknown>[]> =>
    apiClient
      .get<{ items: Record<string, unknown>[] }>(`/approval/project/${projectId}/spend-requests`)
      .then((r) => r.data.items ?? []),

  getApproverQueue: (projectId: string): Promise<Record<string, unknown>[]> =>
    apiClient
      .get<{ items: Record<string, unknown>[] }>(`/approval/project/${projectId}/approver-queue`)
      .then((r) => r.data.items ?? []),

  getMySpendRequests: (projectId: string): Promise<Record<string, unknown>[]> =>
    apiClient
      .get<{ items: Record<string, unknown>[] }>(`/approval/project/${projectId}/my-requests`)
      .then((r) => r.data.items ?? []),

  getSpendRequest: (id: string): Promise<SpendRequest> =>
    apiClient
      .get<{ spendRequest: SpendRequest }>(`/approval/spend-request/${id}`)
      .then((r) => r.data.spendRequest),

  // Full ask detail in one GET: the raw spend_request row plus its raw spend_item,
  // evidence_package and spend_request_document rows. Callers normalise via
  // domain/normalizeSpend so the ask resolves to the same shape/id the Inbox
  // linked with. (id must be numeric — the backend 400s on non-integer ids, so
  // only call this outside demo mode.)
  getSpendRequestDetail: (
    id: string,
  ): Promise<{
    spendRequest: Record<string, unknown> | null;
    items: Record<string, unknown>[];
    evidence: Record<string, unknown>[];
    documents: Record<string, unknown>[];
  }> =>
    apiClient
      .get<{
        spendRequest: Record<string, unknown> | null;
        items: Record<string, unknown>[];
        evidence: Record<string, unknown>[];
        documents?: Record<string, unknown>[];
      }>(`/approval/spend-request/${id}`)
      .then((r) => ({
        spendRequest: r.data.spendRequest ?? null,
        items: r.data.items ?? [],
        evidence: r.data.evidence ?? [],
        documents: r.data.documents ?? [],
      })),

  raiseSpend: (input: RaiseSpendInput): Promise<SpendRequest> =>
    apiClient
      .post<{ spendRequest: SpendRequest }>("/approval/spend-request", {
        projectId: input.projectId,
        subProjectId: input.subProjectId,
        milestoneId: input.milestoneId,
        title: input.title,
        purpose: input.purpose ?? input.description,
        amount: input.amount,
        currency: input.currency,
        targetSupplier: input.targetSupplier ?? input.supplier,
      })
      .then((r) => r.data.spendRequest),

  // A follow-up step after raiseSpend, not folded into it - mirrors how
  // evidence is submitted as its own action. Real files, not base64 - these
  // previously never reached the backend at all (silently dropped).
  uploadSpendRequestDocuments: (spendRequestId: string | number, files: File[]): Promise<void> => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f, f.name));
    return apiClient
      .post(`/approval/spend-request/${spendRequestId}/documents`, form, { headers: { "Content-Type": undefined } })
      .then(() => undefined);
  },

  commitSpendRequest: (id: string): Promise<SpendRequest> =>
    apiClient
      .post<{ spendRequest: SpendRequest }>(`/approval/spend-request/${id}/commit`)
      .then((r) => r.data.spendRequest),

  addSpendItem: (
    askId: string,
    input: { amount: number; supplier?: string; note?: string },
  ): Promise<SpendItem> =>
    apiClient
      .post<{ spendItem: SpendItem }>(`/approval/spend-request/${askId}/items`, input)
      .then((r) => r.data.spendItem),

  // Spend-request lifecycle — matches the actual backend router at /api/approval/
  //
  // `reason` is threaded through approve/confirm as well as reject: the compliance
  // pack requires a written reason for EVERY transition above
  // approval.reasonRequiredAboveAmount (100M on uganda-sovereignty), enforced in
  // transitionSpendRequest. These two used to post no body at all, so the server
  // read req.body?.reason as undefined and threw on every ask over that amount -
  // i.e. on every ask that clears the 400M Stratus gate. Optional, because a
  // below-threshold ask genuinely needs no justification.
  approveSpendRequest: (id: string, reason?: string): Promise<SpendRequest> =>
    apiClient.post<SpendRequest>(`/approval/spend-request/${id}/approve`, { reason }).then((r) => r.data),

  rejectSpendRequest: (id: string, reason: string): Promise<SpendRequest> =>
    apiClient.post<SpendRequest>(`/approval/spend-request/${id}/reject`, { reason }).then((r) => r.data),

  sendBackSpendRequest: (id: string, comment: string): Promise<SpendRequest> =>
    apiClient.post<SpendRequest>(`/approval/spend-request/${id}/send-back`, { comment }).then((r) => r.data),

  confirmSpendRequest: (id: string, reason?: string): Promise<SpendRequest> =>
    apiClient.post<SpendRequest>(`/approval/spend-request/${id}/confirm`, { reason }).then((r) => r.data),

  // Disbursement lifecycle — /api/disbursement/ (backend endpoints to be built by Josh)
  approveDisbursementRequest: (id: string): Promise<unknown> =>
    apiClient.post(`/disbursement/${id}/approve`).then((r) => r.data),

  queryDisbursementRequest: (id: string, note: string): Promise<unknown> =>
    apiClient.post(`/disbursement/${id}/query`, { note }).then((r) => r.data),

  holdDisbursementRequest: (id: string, note: string): Promise<unknown> =>
    apiClient.post(`/disbursement/${id}/hold`, { note }).then((r) => r.data),

  routeDisbursementRequest: (id: string, note?: string): Promise<unknown> =>
    apiClient.post(`/disbursement/${id}/route`, { note }).then((r) => r.data),

  // Oversight — leak state (backend endpoints to be built by Josh)
  setLeakState: (id: string, state: string, note?: string): Promise<unknown> =>
    apiClient.patch(`/oversight/leaks/${id}/state`, { state, note }).then((r) => r.data),

  // Oversight — spend verification (assurance ladder)
  verifySpendRequest: (id: string): Promise<unknown> =>
    apiClient.post(`/oversight/spend/${id}/verify`).then((r) => r.data),

  // Compliance — declarations (backend endpoints to be built by Josh)
  fileDeclaration: (id: string): Promise<unknown> =>
    apiClient.post(`/compliance/declaration/${id}/file`).then((r) => r.data),

  // KYC — document acceptance toggle + final decision (backend endpoints to be built by Josh)
  kycSetDocAccepted: (reviewId: string, docKey: string, accepted: boolean): Promise<unknown> =>
    apiClient.patch(`/kyc/${reviewId}/documents/${docKey}`, { accepted }).then((r) => r.data),

  kycDecide: (reviewId: string, decision: string, note?: string): Promise<unknown> =>
    apiClient.post(`/kyc/${reviewId}/decision`, { decision, note }).then((r) => r.data),

  // AI findings — Heimdall accept / dismiss (backend endpoints to be built by Josh)
  acceptAiFinding: (id: string): Promise<unknown> =>
    apiClient.post(`/ai/findings/${id}/accept`).then((r) => r.data),

  dismissAiFinding: (id: string, reason: string): Promise<unknown> =>
    apiClient.post(`/ai/findings/${id}/dismiss`, { reason }).then((r) => r.data),

  // ── Authority grants (IL-94) ─────────────────────────────────────────────
  updateGrantThreshold: (grantId: string, threshold: number): Promise<void> =>
    apiClient.patch(`/authority-grant/${grantId}`, { selfAuthThreshold: threshold }).then(() => undefined),

  // ── Connection / mobile money (IL-102) ───────────────────────────────────
  syncAdapter: (adapterId: string): Promise<void> =>
    apiClient.post(`/mobile-money/adapters/${adapterId}/sync`).then(() => undefined),
  setAdapterStatus: (adapterId: string, status: "active" | "paused"): Promise<void> =>
    apiClient.patch(`/mobile-money/adapters/${adapterId}/status`, { status }).then(() => undefined),

  // ── Spend items (line items) ─────────────────────────────────────────────
  getSpendItems: (askId: string): Promise<SpendItem[]> =>
    apiClient
      .get<{ items: SpendItem[] }>(`/approval/spend-request/${askId}`)
      .then((r) => r.data.items ?? []),

  // ── Evidence ────────────────────────────────────────────────────────────
  getEvidence: (askId: string): Promise<EvidenceItem[]> =>
    apiClient
      .get<{ evidence: EvidenceItem[] }>(`/approval/spend-request/${askId}`)
      .then((r) => r.data.evidence ?? []),

  // Real multipart upload - the file goes to Cloudinary server-side
  // (approval.js + evidenceFileStore.js), not embedded as base64 text in
  // `notes` like before. `Content-Type: undefined` drops the instance's
  // default `application/json` header so the browser sets the correct
  // `multipart/form-data; boundary=...` one itself.
  submitEvidence: (input: EvidenceInput): Promise<EvidenceItem> => {
    const form = new FormData();
    if (input.note) form.append("notes", input.note);
    if (input.file) form.append("file", input.file, input.file.name);
    if (input.lat != null && input.lng != null) {
      form.append("geo", JSON.stringify({ lat: input.lat, lng: input.lng }));
    }
    return apiClient
      .post<{ evidence: EvidenceItem }>(`/approval/spend-request/${input.askId}/evidence`, form, {
        headers: { "Content-Type": undefined },
      })
      .then((r) => r.data.evidence);
  },

  // ── Oversight (ZEUS-91/92/93) ────────────────────────────────────────────
  getOversightDashboard: (programmeId: string) =>
    apiClient
      .get(`/oversight/${programmeId}/dashboard`)
      .then((r) => r.data),

  getOversightLeaks: (programmeId: string) =>
    apiClient.get(`/oversight/${programmeId}/leaks`).then((r) => r.data),

  getReconciliation: (programmeId: string) =>
    apiClient
      .get(`/oversight/${programmeId}/reconciliation`)
      .then((r) => r.data),

  getGeography: (programmeId: string) =>
    apiClient
      .get(`/oversight/${programmeId}/geography`)
      .then((r) => r.data),

  getOversightKpis: (programmeId: string) =>
    apiClient.get(`/oversight/${programmeId}/kpis`).then((r) => r.data),

  getVarianceTrend: (programmeId: string, opts?: { granularity?: string; lookback?: number }) =>
    apiClient
      .get(`/oversight/${programmeId}/variance-trend`, { params: opts })
      .then((r) => r.data),

  getLeakageRisk: (programmeId: string) =>
    apiClient
      .get(`/oversight/${programmeId}/leakage-risk`)
      .then((r) => r.data),

  // ── Reports ─────────────────────────────────────────────────────────────
  getReports: (programmeId: string): Promise<Report[]> =>
    apiClient
      .get<Report[]>(`/report`, { params: { programmeId } })
      .then((r) => r.data),

  createReport: (input: {
    programmeid: string;
    title: string;
    type: string;
    scope: string;
    projectid?: string | null;
    audience?: string;
    periodlabel: string;
    periodstart: string;
    periodend: string;
    sections: Array<{ heading: string; body: string }>;
    draftedbyai?: boolean;
    limiteddata?: boolean;
    visibleto?: string[];
    notes?: string;
  }): Promise<{ id: string }> =>
    apiClient.post<{ id: string }>("/report", input).then((r) => r.data),

  approveReport: (reportId: string): Promise<void> =>
    apiClient.patch(`/report/${reportId}/approve`).then(() => undefined),

  getSiteVisits: (workspaceId: string): Promise<SiteVisit[]> =>
    apiClient
      .get<SiteVisit[]>("/site-visit", { params: { programmeId: workspaceId } })
      .then((r) => r.data),

  getAuthorityGrants: (programmeId: string): Promise<{ grants: AuthorityGrant[] }> =>
    apiClient
      .get<{ grants: AuthorityGrant[] }>("/authority-grant", { params: { programmeId } })
      .then((r) => r.data),

  // ── Site visits ─────────────────────────────────────────────────────────
  startSiteVisit: (input: {
    programmeid: string;
    projectid?: string | null;
    milestoneid?: number | null;
    checklist: Array<{ id: string; label: string; judgement: string; captureIds: string[] }>;
  }): Promise<{ id: string }> =>
    apiClient.post<{ id: string }>("/site-visit", input).then((r) => r.data),

  beginSiteVisit: (visitId: string): Promise<void> =>
    apiClient.patch(`/site-visit/${visitId}/begin`).then(() => undefined),

  setChecklistJudgement: (visitId: string, itemId: string, judgement: string): Promise<void> =>
    apiClient.patch(`/site-visit/${visitId}/judgement`, { itemId, judgement }).then(() => undefined),

  // Real multipart upload for the photo (see server/routers/siteVisit.js) -
  // `url` is accepted for backward-compat callers but is no longer what
  // reaches the server for a photo; the real File does. `Content-Type:
  // undefined` drops the instance's default application/json header so the
  // browser sets the correct multipart boundary itself.
  addVisitCapture: (
    visitId: string,
    input: { type: "photo" | "note"; checklistItemId?: string; note?: string; file?: File; lat?: number; lng?: number },
  ): Promise<{ captureId: string }> => {
    const form = new FormData();
    form.append("type", input.type);
    if (input.checklistItemId) form.append("checklistItemId", input.checklistItemId);
    if (input.note) form.append("note", input.note);
    if (input.file) form.append("file", input.file, input.file.name);
    if (input.lat != null) form.append("lat", String(input.lat));
    if (input.lng != null) form.append("lng", String(input.lng));
    return apiClient
      .patch<{ captureId: string }>(`/site-visit/${visitId}/capture`, form, { headers: { "Content-Type": undefined } })
      .then((r) => r.data);
  },

  syncVisitQuestions: (visitId: string): Promise<void> =>
    apiClient.patch(`/site-visit/${visitId}/sync-questions`).then(() => undefined),

  completeSiteVisit: (visitId: string, summary?: string): Promise<void> =>
    apiClient.patch(`/site-visit/${visitId}/complete`, { summary }).then(() => undefined),

  generateVisitReport: (
    visitId: string,
    input: { title: string; programmeid: string; sections: Array<{ heading: string; body: string }> },
  ): Promise<{ reportId: string }> =>
    apiClient.post<{ reportId: string }>(`/site-visit/${visitId}/report`, input).then((r) => r.data),

  approveVisitReport: (visitId: string): Promise<void> =>
    apiClient.patch(`/site-visit/${visitId}/approve-report`).then(() => undefined),

  // ── Site-visit question template (custom questions) ───────────────────────
  getSiteVisitQuestions: (programmeId: string): Promise<SiteVisitQuestion[]> =>
    apiClient
      .get<SiteVisitQuestion[]>("/site-visit-question", { params: { programmeId } })
      .then((r) => r.data),

  addSiteVisitQuestion: (input: { programmeid: string; label: string }): Promise<SiteVisitQuestion> =>
    apiClient.post<SiteVisitQuestion>("/site-visit-question", input).then((r) => r.data),

  updateSiteVisitQuestion: (id: string, label: string): Promise<SiteVisitQuestion> =>
    apiClient.patch<SiteVisitQuestion>(`/site-visit-question/${id}`, { label }).then((r) => r.data),

  removeSiteVisitQuestion: (id: string): Promise<void> =>
    apiClient.delete(`/site-visit-question/${id}`).then(() => undefined),

  // ── People + orgs ───────────────────────────────────────────────────────
  getOrganisation: (id: string): Promise<Organisation> =>
    apiClient.get<Organisation>(`/charity/${id}`).then((r) => r.data),

  getPerson: (id: string): Promise<Person> =>
    apiClient.get<Person>(`/user/${id}`).then((r) => r.data),

  // ── Audit trail ─────────────────────────────────────────────────────────
  // The tamper-evident activity log, read from `ledger_event` via
  // /api/audit (server/routers/auditTrail.js). This used to point at the legacy
  // /transaction donations router and was never called by anything — every audit
  // screen rendered the static demo seed blob instead, which is why a real
  // user's actions were recorded by the backend but never showed up.
  //
  // Scope params are a filter, not a permission: the server decides what this
  // user may see from their own role and memberships.
  getAuditTrail: (scope: {
    programmeId?: string;
    projectId?: string;
    subProjectId?: string;
    limit?: number;
    offset?: number;
  }): Promise<AuditEvent[]> =>
    apiClient
      .get<{ events: AuditEvent[] }>(`/audit`, { params: scope })
      .then((r) => r.data.events ?? []),

  // ── Mobile money (ZEUS-24/25/26) ─────────────────────────────────────────
  getMobileMoneyStatus: () =>
    apiClient.get("/mobile-money/status").then((r) => r.data),

  getMobileMoneyAdapterConfigs: (accountId: string) =>
    apiClient
      .get(`/mobile-money/adapters`, { params: { accountId } })
      .then((r) => r.data),

  syncMobileMoneyAdapter: (adapterConfigId: string) =>
    apiClient
      .post(`/mobile-money/adapters/${adapterConfigId}/sync`)
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Contractor onboarding (ZEUS cascade — Tier-2 prime wizard)
//
// peek/start/forward are public (no JWT). After start() the caller stores the
// returned token via the auth store, so the request interceptor attaches it to
// the subsequent authed step/complete calls automatically.
// ---------------------------------------------------------------------------

export interface OnboardingInvite {
  email: string;
  /** Display name the inviter entered for this person, if provided. */
  name: string | null;
  /** Who issued this invite — shown so the invitee knows who to contact. */
  inviterName: string | null;
  inviterEmail: string | null;
  intent: string;
  role: string | null;
  scope: string | null;
  organisationId: number | null;
  projectId: number | null;
  programmeId: number | null;
  /** The project this invite is scoped to, resolved server-side. Null only when
   * the invite carries no project (or the row is gone) — the wizard renders
   * these rather than guessing the assigned project from seed data. */
  projectName: string | null;
  projectSlug: string | null;
  projectDistrict: string | null;
  projectBudgeted: number | null;
  programmeName: string | null;
  forwardedFromEmail: string | null;
  payload: Record<string, unknown>;
}

export interface WizardSession {
  id: number;
  magiclinkid: number;
  wizardtype: string;
  userid: number | null;
  organisationid: number | null;
  currentstep: string;
  completedsteps: string[];
  stepdata: Record<string, Record<string, unknown>>;
  status: "in_progress" | "completed" | "abandoned";
  startedat: string;
  completedat: string | null;
  updatedat: string;
}

export type OnboardingLinkState = "active" | "consumed" | "expired" | "revoked" | null;

export interface OnboardingPeek {
  state: OnboardingLinkState;
  invite: OnboardingInvite;
  requiresUserCreation: boolean;
  // Which wizard this invite runs; null = no cascade wizard (stratus/funder).
  wizardType: "tier2_prime" | "tier3_sub_new" | "tier3_sub_returning" | "tier4_reporter" | null;
}

export interface OnboardingStartInput {
  firstname?: string;
  lastname?: string;
  password?: string;
}

export interface OnboardingUser {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  usertype: string;
  username?: string;
}

export interface OnboardingStartResult {
  token: string;
  user: OnboardingUser;
  session: WizardSession;
  invite: OnboardingInvite;
}

export const onboardingApi = {
  // Public — render the invite context without consuming the link.
  peek: (token: string): Promise<OnboardingPeek> =>
    apiClient.get<OnboardingPeek>(`/onboarding/${token}`).then((r) => r.data),

  // Public — redeem, create/resolve the user, begin the wizard, return a JWT.
  start: (token: string, input: OnboardingStartInput = {}): Promise<OnboardingStartResult> =>
    apiClient
      .post<OnboardingStartResult>(`/onboarding/${token}/start`, input)
      .then((r) => r.data),

  // Public — "this is not me": forward the invite to the right person.
  forward: (token: string, input: { email: string; name?: string }): Promise<{ ok: boolean }> =>
    apiClient.post<{ ok: boolean }>(`/onboarding/${token}/forward`, input).then((r) => r.data),

  // Authed — rehydrate an in-progress session (resume flow).
  resume: (token: string): Promise<{ session: WizardSession; invite: OnboardingInvite }> =>
    apiClient
      .get<{ session: WizardSession; invite: OnboardingInvite }>(`/onboarding/session/resume`, {
        params: { token },
      })
      .then((r) => r.data),

  // Authed — merge draft data for a step without advancing.
  saveStep: (
    sessionId: number,
    step: string,
    data: Record<string, unknown>,
  ): Promise<{ session: WizardSession }> =>
    apiClient
      .patch<{ session: WizardSession }>(`/onboarding/session/${sessionId}/step/${step}`, { data })
      .then((r) => r.data),

  // Authed — commit a step's domain mutation(s) and advance the machine.
  completeStep: (
    sessionId: number,
    step: string,
    data: Record<string, unknown>,
  ): Promise<{ session: WizardSession }> =>
    apiClient
      .post<{ session: WizardSession }>(
        `/onboarding/session/${sessionId}/step/${step}/complete`,
        { data },
      )
      .then((r) => r.data),

  // Authed — import parsed bank-statement CSV rows (bank step, method
  // "statement"). Rows land in the ZEUS-29 ledger; org bank state → pending.
  // Does not advance the wizard.
  uploadStatement: (
    sessionId: number,
    payload: { rows: StatementRowInput[]; fileMeta: StatementFileMeta },
  ): Promise<{ result: StatementImportResult }> =>
    apiClient
      .post<{ result: StatementImportResult }>(
        `/onboarding/session/${sessionId}/statement`,
        payload,
      )
      .then((r) => r.data),

  // Authed — set the invited user's account password (Account step).
  setPassword: (sessionId: number, password: string): Promise<{ ok: boolean }> =>
    apiClient
      .post<{ ok: boolean }>(`/onboarding/session/${sessionId}/password`, { password })
      .then((r) => r.data),

  // Authed — finalise onboarding; returns the landing route.
  completeSession: (sessionId: number): Promise<{ session: WizardSession; landing: string }> =>
    apiClient
      .post<{ session: WizardSession; landing: string }>(`/onboarding/session/${sessionId}/complete`)
      .then((r) => r.data),

  // Authed — create an invite (spec §9.5). Returns the raw token; the caller
  // builds the secure onboarding link. Email/WhatsApp delivery is not automated.
  createInvite: (input: CreateInviteInput): Promise<CreateInviteResult> =>
    apiClient.post<CreateInviteResult>("/onboarding/invite", input).then((r) => r.data),

  // Authed (oversight only) — the approval gate (spec §9.6). List orgs awaiting
  // sign-off, then approve (→ active) or reject (→ suspended; terminal → revoked).
  listGates: (): Promise<{ gates: GateOrg[] }> =>
    apiClient.get<{ gates: GateOrg[] }>("/onboarding/gates").then((r) => r.data),
  // The full submission behind one gate row — what the contractor entered and
  // uploaded during onboarding (organisation detail, bank/statement, documents,
  // invites, contact, queries). Feeds the approval drawer off demo mode.
  getGate: (orgId: string): Promise<GateDetail> =>
    apiClient.get<GateDetail>(`/onboarding/gates/${orgId}`).then((r) => r.data),
  approveGate: (
    orgId: string,
    reason?: string,
  ): Promise<{ organisation: { id: number; status: string }; emailSent: boolean }> =>
    apiClient
      .post(`/onboarding/gates/${orgId}/approve`, { reason: reason ?? null })
      .then((r) => r.data),
  rejectGate: (
    orgId: string,
    reason: string,
    terminal = false,
  ): Promise<{ organisation: { id: number; status: string }; emailSent: boolean }> =>
    apiClient
      .post(`/onboarding/gates/${orgId}/reject`, { reason, terminal })
      .then((r) => r.data),
};

// GET /onboarding/gates/:orgId — the document-level onboarding submission the
// oversight reviewer signs off against. Collections are always present (possibly
// empty), so "nothing was submitted" is distinguishable from "not loaded".
export interface GateDetail {
  organisation: {
    id: number;
    name: string;
    legalName: string | null;
    tradingName: string | null;
    email: string | null;
    phone: string | null;
    organisationType: string | null;
    tier: string | null;
    status: string;
    pipelineStage: string | null;
    daysInStage: number | null;
    awaitingGateSince: string | null;
    kycState: string;
    kycTier: string | null;
    kycVerifiedAt: string | null;
    bankVerificationState: string;
    bankVerifiedAt: string | null;
    createdAt: string;
    updatedAt: string;
    parentOrganisationId: number | null;
    parentOrganisationName: string | null;
    registrationId: string | null;
    countryOfIncorporation: string | null;
    taxId: string | null;
    registrationVerified: boolean;
  };
  submission: {
    sessionId: number | string;
    wizardType: string;
    status: string;
    currentStep: string;
    completedSteps: string[];
    startedAt: string;
    completedAt: string | null;
    updatedAt: string;
  } | null;
  bank: {
    state: string;
    verifiedAt: string | null;
    method: string | null;
    statementFile: {
      name: string | null;
      sha256: string | null;
      importedCount: number | null;
      dedupedCount: number | null;
      skippedCount: number | null;
    } | null;
  };
  declaration: { acknowledged: boolean | null };
  primaryContact: {
    name: string | null;
    email: string | null;
    phone: string | null;
    userId: number | null;
    userType: string | null;
  };
  members: {
    userId: number;
    name: string | null;
    email: string;
    phone: string | null;
    userType: string | null;
    roles: string[];
    scope: string | null;
    joinedAt: string | null;
  }[];
  documents: {
    key: string;
    label: string;
    required: boolean;
    fileName: string | null;
    detail: string | null;
    checksum: string | null;
    accepted: boolean;
    submitted: boolean;
  }[];
  invites: {
    id: string;
    email: string;
    name: string | null;
    role: string;
    projectId: number | null;
    projectName: string | null;
    status: "queued" | "sent" | "redeemed" | "expired" | "revoked";
    createdAt: string;
    dispatchedAt: string | null;
  }[];
  kyc: {
    id: number | string;
    tier: string;
    state: string;
    submittedAt: string;
    reviewedAt: string | null;
    reviewerNote: string | null;
    comments: {
      id: string;
      body: string;
      visibility: string;
      createdAt: string;
      authorName: string | null;
    }[];
  } | null;
  projects: {
    id: number;
    name: string | null;
    role: string | null;
    scope: string | null;
    status: string | null;
  }[];
  statusHistory: {
    id: string;
    fromStatus: string | null;
    toStatus: string;
    reason: string | null;
    createdAt: string;
  }[];
}

// A row from GET /onboarding/gates — an org awaiting oversight approval.
export interface GateOrg {
  id: number;
  name: string | null;
  legalname: string | null;
  tier: string | null;
  parentorganisationid: number | null;
  kycstate: string | null;
  bankverificationstate: string | null;
  createdat: string;
  updatedat: string;
}

// One parsed bank-statement line, normalized client-side from the CSV.
export interface StatementRowInput {
  importReference: string; // fileSha-prefixed content key — dedup identity
  direction: "inflow" | "outflow";
  amount: number; // always positive; direction carries the sign
  currency: string;
  occurredAt: string; // ISO date
  description?: string;
  counterpartyName?: string;
}

export interface StatementFileMeta {
  name: string;
  sha256: string;
}

export interface StatementImportResult {
  accountId: number;
  imported: number;
  deduped: number;
  skipped: number;
  total: number;
}

export interface CreateInviteInput {
  role: string; // cascade role: sub_lead | prime_member | sub_member | reporter | prime_lead
  inviteeEmail: string;
  inviteeName?: string;
  projectId: number | string;
  message?: string;
}

export interface CreateInviteResult {
  /** The onboarding token, or null when the invite was queued (deferred until
   * the inviter's org is approved) — no link exists yet in that case. */
  token: string | null;
  role: string;
  inviteeEmail: string;
  returningOrg: boolean;
  /** True for a staff-role invite (project member / reporter / sub-project
   * member) whose email matched an existing Impact Ledger user: they were
   * added straight to the project instead of getting an onboarding link. */
  returningUser: boolean;
  /** Whether the invite email was actually delivered to the SMTP server; the
   * invite (and its link) exists either way, so false means "share manually". */
  emailSent: boolean;
  /** True when the inviter's org isn't approved yet: the invitee's details are
   * queued and will be emailed automatically once the org is approved. No link
   * or token is issued until then (spec §9). */
  queued?: boolean;
}

// "Add members" — an existing platform user, matched by name/email search
// rather than the exact-email-only lookup Invite uses.
export interface SearchableUser {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
}

export interface AddMemberInput {
  userId: number;
  role: string; // cascade role, same vocabulary as CreateInviteInput.role
  organisationId?: number | string;
  scope?: string;
}

export interface AddMemberResult {
  membership: { id: number; userid: number; projectid: number; role: string; status: string };
  /** True if this person already had a projectmembership row on this
   * project (reused as-is) rather than one being freshly created. */
  alreadyMember: boolean;
}

/** A real projectmembership row joined to its user — GET /project-membership/project/:projectid.
 * `role` is the real cascade role key (prime_lead/sub_lead/prime_member/sub_member/reporter, etc.) —
 * label it with roleLabelFor before display, don't render it raw. */
export interface ProjectMemberRow {
  membershipId: number;
  userId: number;
  name: string;
  email: string;
  role: string;
  status: "pending" | "active" | "suspended" | "revoked";
  organisationName: string | null;
}

export interface CreateZeusProjectInput {
  name: string;
  description?: string;
  justification: string;
  status?: "draft" | "review" | "published";
  programmeId?: number;
  parentProjectId?: number;
  /** Set when this creation accepts a pending project-creation request
   * (ZEUS-129) — atomically links the two server-side. */
  requestId?: number;
}

export interface EditZeusProjectInput {
  name?: string;
  description?: string;
  region?: string;
  district?: string;
  budgeted?: number;
  justification: string;
}

export interface ChangeZeusProjectStatusInput {
  status: "draft" | "review" | "published";
  justification: string;
}

export interface LeadContractorCandidate {
  id: number;
  name: string;
  email: string;
  organisationtype: string;
  status: string;
}

export interface AssignLeadContractorInput {
  organisationId: number;
  justification: string;
}

export interface ProjectOrganisationSummary {
  id: number;
  name: string;
  organisationType: string;
  status: string;
  kycState: string;
}

export interface ProjectOrganisationsResult {
  project: { id: number; name: string; leadContractor: ProjectOrganisationSummary | null } | null;
  subProjects: { id: number; name: string; leadContractor: ProjectOrganisationSummary | null }[];
}

export interface CreateZeusProjectResult {
  project: {
    // number from the real API; string for a demo-mode-simulated id (see
    // useProjectCreation.ts) — honestly widened rather than cast around.
    id: number | string;
    name: string;
    title: string;
    status: string;
    [key: string]: unknown;
  };
}

export interface SubmitProjectCreationRequestInput {
  /** Omit to let the server default to prime_lead (server/routers/projectCreationRequest.js) —
   * the frontend has no reliable single source for the caller's exact cascade role here. */
  requesterRole?: string;
  proposedName: string;
  proposedDescription: string;
  justification: string;
  programmeId?: number;
  parentProjectId?: number;
}

export interface SubmitProjectCreationRequestResult {
  request: {
    // number from the real API; string for a demo-mode-simulated id.
    id: number | string;
    status: string;
    [key: string]: unknown;
  };
}

// ZEUS-138 — a pending (or decided) project-creation request, as returned by
// the stratus_admin review queue. Real-API only; no demo-mode equivalent.
export interface ProjectCreationRequest {
  id: number;
  requesteruserid: number;
  requesterrole: string;
  proposedname: string;
  proposeddescription: string;
  justification: string;
  programmeid: number | null;
  parentprojectid: number | null;
  status: "pending" | "accepted" | "rejected";
  rejectionreason: string | null;
  createdat: string;
  [key: string]: unknown;
}

// ZEUS-144 — a contractor/subcontractor's proposed edit to a published
// project, as returned by the stratus_admin review queue (scoped to one
// project). Real-API only; no demo-mode equivalent.
export interface SubmitProjectEditRequestInput {
  projectId: number;
  justification: string;
  changes: Partial<{
    name: string;
    description: string;
    region: string;
    district: string;
    budgeted: number;
  }>;
}

export interface ProjectEditRequest {
  id: number;
  projectid: number;
  submitteduserid: number;
  submitterrole: string;
  justification: string;
  previousstate: Record<string, unknown>;
  proposedstate: Record<string, unknown>;
  changediff: Record<string, { from: unknown; to: unknown }>;
  status: "pending" | "approved" | "rejected";
  rejectionreason: string | null;
  createdat: string;
  [key: string]: unknown;
}

export interface GovernanceHistoryEvent {
  id: string;
  eventtype: string;
  actorid: number | null;
  actorrole: string | null;
  justification: string | null;
  previousstate: Record<string, unknown> | null;
  newstate: Record<string, unknown> | null;
  changediff: Record<string, unknown> | null;
  requestid: string | null;
  createdat: string;
}

// ---------------------------------------------------------------------------
// Trace Source — raw row shapes from server/routers/trace.js. These are
// intentionally left close to the DB row shape (lowercase columns, ids as
// number|string since spend_request.id is a Postgres bigint and comes back
// as a string) — domain/trace.ts's async resolvers map them onto the same
// TraceResult/TraceNode shapes resolveTrace already produces from seed data.
// ---------------------------------------------------------------------------

type Id = number | string;

export interface TraceDisbursementRow {
  id: Id;
  milestoneid: Id | null;
  amount: number | string;
  state: string;
  basisaskids: Id[] | null;
  bankingtransactionref?: string | null;
  [key: string]: unknown;
}

export interface TraceMilestoneRow {
  id: Id;
  projectid: Id;
  name: string;
  status: string;
  evidencerequirements?: string[] | null;
  [key: string]: unknown;
}

export interface TraceProjectRow {
  id: Id;
  name: string;
  title?: string | null;
  parentprojectid: Id | null;
  budgeted?: number | string | null;
  spent?: number | string | null;
  evidenced?: number | string | null;
  [key: string]: unknown;
}

export interface TraceSpendRequestRow {
  id: Id;
  projectid: Id;
  subprojectid: Id | null;
  milestoneid: Id | null;
  title: string;
  amount: number | string;
  state: string;
  targetsupplier?: string | null;
  [key: string]: unknown;
}

export interface TraceEvidenceRow {
  id: Id;
  spendrequestid: Id;
  notes: string | null;
  geolatitude: number | string | null;
  geolongitude: number | string | null;
  geocapturedat: string | null;
  filetype: "photo" | "pdf" | "document" | null;
  fileurl: string | null;
  filename: string | null;
  filebytes: number | null;
  createdat: string;
}

export interface TraceDisbursementDetail {
  disbursement: TraceDisbursementRow;
  milestone: TraceMilestoneRow | null;
  project: TraceProjectRow | null;
  basisAsks: TraceSpendRequestRow[];
}

export interface TraceMilestoneDetail {
  milestone: TraceMilestoneRow;
  project: TraceProjectRow | null;
  spendRequests: TraceSpendRequestRow[];
  disbursement: TraceDisbursementRow | null;
}

export interface TraceProjectDetail {
  project: TraceProjectRow;
  parentProject: TraceProjectRow | null;
  childProjects: TraceProjectRow[];
  spendRequests: TraceSpendRequestRow[];
}

export interface TraceSpendRequestDetail {
  spendRequest: TraceSpendRequestRow;
  project: TraceProjectRow | null;
  subProject: TraceProjectRow | null;
  milestone: TraceMilestoneRow | null;
  disbursement: TraceDisbursementRow | null;
}

export interface TraceSiblingsResult<T> {
  siblings: T[];
  total: number;
}

export type TraceAuditEventType =
  | "trace.opened"
  | "trace.walked_to"
  | "trace.evidence_opened"
  | "trace.closed";

export const traceApi = {
  getDisbursement: (id: Id): Promise<TraceDisbursementDetail> =>
    apiClient.get<TraceDisbursementDetail>(`/trace/disbursement/${id}`).then((r) => r.data),

  getMilestone: (id: Id): Promise<TraceMilestoneDetail> =>
    apiClient.get<TraceMilestoneDetail>(`/trace/milestone/${id}`).then((r) => r.data),

  getProject: (id: Id): Promise<TraceProjectDetail> =>
    apiClient.get<TraceProjectDetail>(`/trace/project/${id}`).then((r) => r.data),

  getProjectSiblings: (id: Id, opts?: { limit?: number; offset?: number }): Promise<TraceSiblingsResult<TraceProjectRow>> =>
    apiClient
      .get<TraceSiblingsResult<TraceProjectRow>>(`/trace/project/${id}/siblings`, { params: opts })
      .then((r) => r.data),

  getSpendRequest: (id: Id): Promise<TraceSpendRequestDetail> =>
    apiClient.get<TraceSpendRequestDetail>(`/trace/spend-request/${id}`).then((r) => r.data),

  getSpendRequestSiblings: (id: Id, opts?: { limit?: number; offset?: number }): Promise<TraceSiblingsResult<TraceSpendRequestRow>> =>
    apiClient
      .get<TraceSiblingsResult<TraceSpendRequestRow>>(`/trace/spend-request/${id}/siblings`, { params: opts })
      .then((r) => r.data),

  getEvidence: (spendRequestId: Id): Promise<{ evidence: TraceEvidenceRow[] }> =>
    apiClient.get<{ evidence: TraceEvidenceRow[] }>(`/trace/spend-request/${spendRequestId}/evidence`).then((r) => r.data),

  logEvent: (input: { eventType: TraceAuditEventType; entityType?: string; entityId?: Id; payload?: Record<string, unknown> }): Promise<void> =>
    apiClient.post("/trace/event", input).then(() => undefined),
};
